import React from 'react';
import { getPlatformRequirement, formatFileSize, formatDuration } from '../../utils/platformRequirements';
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { AlertCircle, AlertTriangle } from 'lucide-react';

interface ValidationResult {
  platformId: string;
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface PlatformValidationsProps {
  selectedPlatforms: string[];
  videoDuration: number | null; // in seconds
  fileSize: number | null; // in bytes
  selectedDuration?: number; // selected duration after trimming
  isImage?: boolean;
  onValidationChange?: (hasErrors: boolean, hasWarnings: boolean) => void;
  addEndLogo?: boolean;
  youtubeFallbackEnabled?: boolean;
}

export default function PlatformValidations({
  selectedPlatforms,
  videoDuration,
  fileSize,
  selectedDuration,
  isImage,
  onValidationChange,
  addEndLogo = false,
  youtubeFallbackEnabled = false,
}: PlatformValidationsProps): JSX.Element {
  const END_LOGO_DURATION = 9;
  const validations: ValidationResult[] = selectedPlatforms.map(platformId => {
    const requirement = getPlatformRequirement(platformId);
    if (!requirement) {
      return {
        platformId,
        isValid: true,
        errors: [],
        warnings: [],
      };
    }

    const errors: string[] = [];
    const warnings: string[] = [];
    let durationToCheck = selectedDuration || videoDuration || 0;
    
    if (!isImage && addEndLogo) {
      durationToCheck += END_LOGO_DURATION;
    }

    // Platform specific media support checks
    const platformLower = platformId.toLowerCase();

    // TikTok implementation only supports video for now
    if (isImage && platformLower === 'tiktok') {
      errors.push('TikTok implementation only supports video currently');
    }

    // Discord video warning: non-blocking size warning + contextual info
    // Skip entirely if YouTube Fallback is active (video will be uploaded to YouTube, link shared in Discord)
    if (!isImage && platformLower === 'discord' && !youtubeFallbackEnabled) {
      const LINK_PLATFORMS = ['twitter', 'x', 'x-es', 'youtube', 'tiktok', 'instagram', 'threads', 'bluesky', 'tumblr'];
      const hasOtherPlatform = selectedPlatforms.some(p => LINK_PLATFORMS.includes(p.toLowerCase()));

      if (fileSize && fileSize > requirement.maxFileSize) {
        if (hasOtherPlatform) {
          warnings.push(`File size (${formatFileSize(fileSize)}) exceeds Discord's ${formatFileSize(requirement.maxFileSize)} limit. A link from another platform will be shared instead.`);
        } else {
          warnings.push(`File size (${formatFileSize(fileSize)}) exceeds Discord's ${formatFileSize(requirement.maxFileSize)} limit. The video will be automatically compressed (quality/resolution may be reduced).`);
        }
      } else if (!hasOtherPlatform) {
        warnings.push('Video quality and resolution may be reduced to fit Discord limits. We recommend also publishing to other platforms — we\'ll share a link in Discord instead of uploading the file.');
      }
    }

    if (!isImage && durationToCheck > requirement.maxVideoDuration) {
      errors.push(
        `Duration (${formatDuration(durationToCheck)}) exceeds limit of ${formatDuration(requirement.maxVideoDuration)}`
      );
    }

    // Generic size check: skip for Discord (handled above as warning)
    // Also skip when YouTube Fallback is active for video posts (video will be uploaded to YouTube, link shared)
    if (fileSize && fileSize > requirement.maxFileSize && platformLower !== 'discord' && !(youtubeFallbackEnabled && !isImage)) {
      errors.push(
        `Size (${formatFileSize(fileSize)}) exceeds limit of ${formatFileSize(requirement.maxFileSize)}`
      );
    }

    return {
      platformId,
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  });

  const hasErrors = validations.some(v => v.errors.length > 0);
  const hasWarnings = validations.some(v => v.warnings.length > 0);

  React.useEffect(() => {
    onValidationChange?.(hasErrors, hasWarnings);
  }, [hasErrors, hasWarnings, onValidationChange]);

  const anyValidationNeeded = validations.some(v => v.errors.length > 0 || v.warnings.length > 0);

  if (selectedPlatforms.length === 0 || !anyValidationNeeded) {
    return <></>;
  }

  return (
    <div className="bg-muted/50 border border-border rounded-2xl p-4 space-y-3 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle className="h-4 w-4 text-muted-foreground" />
        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Technical Compliance</span>
      </div>
      
      {validations.map((validation) => {
        const requirement = getPlatformRequirement(validation.platformId);
        if (!requirement || (validation.errors.length === 0 && validation.warnings.length === 0)) return null;

        return (
          <div key={validation.platformId} className="space-y-2">
            {validation.errors.map((err, idx) => (
              <Alert key={`err-${idx}`} variant="destructive" className="bg-red-500/5 border-red-500/20 py-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-3 w-3 text-red-500" />
                  <AlertDescription className="text-[11px] font-bold text-red-400">
                    <span className="uppercase opacity-60 mr-1">{requirement.name}:</span> {err}
                  </AlertDescription>
                </div>
              </Alert>
            ))}
            {validation.warnings.map((warn, idx) => (
              <Alert key={`warn-${idx}`} className="bg-amber-500/5 border-amber-500/20 py-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                  <AlertDescription className="text-[11px] font-bold text-amber-400">
                    <span className="uppercase opacity-60 mr-1">{requirement.name}:</span> {warn}
                  </AlertDescription>
                </div>
              </Alert>
            ))}
          </div>
        );
      })}
    </div>
  );
}
